import { type ChangeEvent, useEffect, useState } from "react"
import { useVectorModal } from "../../../hooks/useVectorMemoryModal"

import { Box, Checkbox, Divider, InputLabel, List, Typography } from "@mui/material"
import { UploadItem } from "./UploadItem"
import { UploadSection } from "./UploadSection"

import { api } from "../../../lib/axios"

import type { TabPanelProps, VectorFileData } from "./vectorModal"
import type { VectorChunkingMethod } from "../../../types/vector"
import type { MutationStatus } from "@tanstack/react-query"

const getFileObject = async (files: Array<File>): Promise<Array<VectorFileData>> => {
	const uploadPromises = files.map(async (file) => {
		const formData = new FormData()
		formData.append('file', file)

		const response = await api.post(
			'/upload/single',
			formData,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		)

		const fileUrl: string = response?.data.data.src 

		return {
			id: crypto.randomUUID().toString(),
			chunking: 'by1000Characters' as const,
			fileName: file.name,
			fileSize: file.size,
			fileUrl,
			metadatas: []
		}
	})

	const uploadResult = await Promise.all(uploadPromises)

	return uploadResult
}

function UploadTabPanel({ selectedTab }: TabPanelProps) {
	const {
		stagingFiles,
		selectedStagingFiles,
		onStagingFilesChange,
		onStagingFilesAdd,
		onSelectedStagingFilesChange,
	} = useVectorModal()

	const [uploadStatus, setUploadStatus] = useState<MutationStatus>('idle')
	const [uploadFiles, setUploadFiles] = useState<Array<File> | null>(null)

	const handleFileUpload = (files: FileList) => {
		setUploadFiles([...files])
	}

	useEffect(() => {
		if (uploadFiles) {
			setUploadStatus('pending')

			getFileObject(uploadFiles)
				.then((stagingFiles) => {
					onStagingFilesAdd(stagingFiles)
					setUploadStatus('success')
				})
				.catch(() => { setUploadStatus('error') })
				.finally(() => { 
					setTimeout(() => {
						setUploadStatus('idle')
					}, 2000)
				})
		}
	}, [uploadFiles, onStagingFilesAdd])

	const handleChunkingMethodChange = (
		fileId: string,
		newChunkingMethod: VectorChunkingMethod
	) => {
		const newFileList = stagingFiles.map((file) => {
			if (file.id === fileId) {
				return {
					...file,
					chunking: newChunkingMethod
				}
			}

			return file
		})

		onStagingFilesChange(newFileList)
	}

	const handleSelectedStagingFilesToggle = (fileId: string) => {
		if (selectedStagingFiles.includes(fileId)) {
			const newSelectedFilesList = selectedStagingFiles.filter((id) => id !== fileId)

			onSelectedStagingFilesChange(newSelectedFilesList)
			return
		}

		onSelectedStagingFilesChange([...selectedStagingFiles, fileId])
	}

	const handleSelectAllStagingFiles = (evt: ChangeEvent<HTMLInputElement>) => {
		const isChecked = evt.target.checked

		if (isChecked) {
			const newSelectedFilesList = stagingFiles.map(({ id }) => id)

			onSelectedStagingFilesChange(newSelectedFilesList)
		} else {
			onSelectedStagingFilesChange([])
		}
	}

	const isAllStagingFilesSelected
		= selectedStagingFiles.length > 0 && selectedStagingFiles.length === stagingFiles.length
	const isIndeterminateStagingFilesSelected
		= selectedStagingFiles.length > 0 && selectedStagingFiles.length < stagingFiles.length

	const disableSelectAllStagingFiles = stagingFiles.length === 0

	return (
		<Box
			role="tabpanel"
			hidden={selectedTab !== 'upload'}
			id="vertical-tabpanel-upload"
			aria-labelledby="vertical-tab-upload"
			sx={{
				width: '100%',

				'&[hidden]': {
					display: 'none',
				},

				'&>*+*': {
					marginBlockStart: '2rem !important'
				}
			}}
		>
			<Box sx={{
				width: '900px',
				marginInline: 'auto',
				paddingBlockStart: '1.5rem',

				'&>*+*': {
					marginBlockStart: '1rem !important'
				}
			}}>
				<Typography
					variant="h2"
					fontWeight={500}
					fontSize={20}
					sx={{ lineHeight: '120%' }}
				>
					Upload Documents
				</Typography>

				<UploadSection 
					uploadStatus={uploadStatus}
					onFileUpload={handleFileUpload}
				/>
			</Box>

			<Divider />

			<Box sx={{
				width: '900px',
				marginInline: 'auto',
			}}>
				{stagingFiles.length === 0 ? (
						<Box sx={{ display: 'grid', placeContent: 'center', textAlign: 'center' }}>
							<Typography>It appers you have not uploaded any files yet!</Typography>
							<Typography>Start by uploading your first file.</Typography>
						</Box>
					) : (
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
								<Typography
									variant="h3"
									fontWeight={500}
									fontSize={20}
								>
									Preparing to Upload
								</Typography>

								<InputLabel
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '0.25rem',
										color: 'black'
									}}
								>
									<Checkbox
										checked={isAllStagingFilesSelected}
										indeterminate={isIndeterminateStagingFilesSelected}
										onChange={handleSelectAllStagingFiles}
										disabled={disableSelectAllStagingFiles}
										id="staging-select-all"
									/>

									<span
										style={{ opacity: disableSelectAllStagingFiles ? '50%' : '100%' }}
										aria-disabled={disableSelectAllStagingFiles}
									>
										Select All
									</span>
								</InputLabel>
							</Box>

							<List sx={{ '&>*+*': { marginBlockStart: '1.25rem' }}}>
								{stagingFiles.map(
									({ chunking, fileName, fileSize, id, metadatas }) => (
										<UploadItem
											// files={stagingFiles}
											key={id}
											checked={selectedStagingFiles.includes(id)}
											chunking={chunking}
											fileName={fileName}
											fileSize={fileSize}
											metadatas={metadatas}
											id={id}
											onSelectedFilesChange={handleSelectedStagingFilesToggle}
											onChunkingMethodChange={handleChunkingMethodChange}
										/>
									))
								}
							</List>
						</Box>
					)
				}
			</Box>

		</Box>
	)
}

export { UploadTabPanel }